import { useEffect, useState } from "react";
import { ShieldCheck, UserPlus, KeyRound, Trash2, Copy, RefreshCw, Crown } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const sydFmt = new Intl.DateTimeFormat("en-AU", { timeZone: "Australia/Sydney", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
const syd = (iso) => { if (!iso) return "never"; try { return sydFmt.format(new Date(iso)); } catch { return "—"; } };
const field = "w-full bg-obsidian-700 border border-white/10 rounded-sm p-3 text-sm outline-none focus:border-neon";
const btn = "inline-flex items-center gap-2 rounded-sm px-4 py-2.5 text-sm font-bold transition-colors disabled:opacity-50";
const err = (e) => e?.response?.data?.detail || "Something went wrong";

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%";
  const arr = crypto.getRandomValues(new Uint32Array(14));
  return Array.from(arr, (n) => chars[n % chars.length]).join("");
};
const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Copied to clipboard"));

const AddMemberForm = ({ onAdded }) => {
  const [form, setForm] = useState({ name: "", email: "", password: genPassword() });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/team", form);
      onAdded(data);
      toast.success(`${data.name} can now sign in at /admin — send them the password`);
      setForm({ name: "", email: "", password: genPassword() });
    } catch (ex) { toast.error(err(ex)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-lg border border-white/10 bg-obsidian-800 p-6" data-testid="team-add-form">
      <div className="mb-5 flex items-center gap-2 text-neon"><UserPlus size={17} /><span className="font-mono text-[11px] uppercase tracking-widest">Add a staff login</span></div>
      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Name</label>
          <input value={form.name} onChange={set("name")} required placeholder="e.g. Marketing Manager" data-testid="team-name-input" className={`mt-1.5 ${field}`} />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Email</label>
          <input value={form.email} onChange={set("email")} required type="email" placeholder="their@email.com" data-testid="team-email-input" className={`mt-1.5 ${field}`} />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Password (8+ chars)</label>
          <div className="mt-1.5 flex gap-2">
            <input value={form.password} onChange={set("password")} required minLength={8} data-testid="team-password-input" className={field} />
            <button type="button" onClick={() => setForm((f) => ({ ...f, password: genPassword() }))} title="Generate" data-testid="team-password-generate" className="rounded-sm border border-white/15 px-3 text-zinc-300 hover:border-neon hover:text-neon"><RefreshCw size={15} /></button>
            <button type="button" onClick={() => copy(form.password)} title="Copy" data-testid="team-password-copy" className="rounded-sm border border-white/15 px-3 text-zinc-300 hover:border-neon hover:text-neon"><Copy size={15} /></button>
          </div>
        </div>
      </div>
      <p className="mt-4 text-xs text-zinc-500">Staff can see leads, projects, settings and Gmail drafts. Only you can add or remove logins, connect or disconnect Gmail, and approve &amp; send replies.</p>
      <button type="submit" disabled={busy} data-testid="team-add-submit" className={`${btn} mt-4 bg-neon text-obsidian hover:bg-neon-hover`}><UserPlus size={15} /> {busy ? "Creating…" : "Create login"}</button>
    </form>
  );
};

const MemberRow = ({ m, isMe, onChanged }) => {
  const [resetting, setResetting] = useState(false);
  const [pw, setPw] = useState("");
  const owner = m.role === "owner";
  const resetPassword = async () => {
    try {
      await api.post(`/admin/team/${m.id}/password`, { password: pw });
      toast.success(`Password updated for ${m.email} — send them the new one`);
      setResetting(false); setPw("");
    } catch (ex) { toast.error(err(ex)); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${m.email}? They will be signed out and can no longer access the admin.`)) return;
    try { await api.delete(`/admin/team/${m.id}`); toast.success("Login removed"); onChanged(); } catch (ex) { toast.error(err(ex)); }
  };
  return (
    <div className="border-b border-white/5 px-5 py-4 last:border-0" data-testid={`team-member-${m.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{m.name || m.email}</span>
            <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${owner ? "bg-gold/15 text-gold" : "bg-white/10 text-zinc-300"}`} data-testid={`team-role-${m.id}`}>
              {owner && <Crown size={9} />} {m.role}
            </span>
            {isMe && <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">you</span>}
          </div>
          <p className="text-xs text-zinc-500 mt-0.5 truncate">{m.email}</p>
          <p className="font-mono text-[10px] text-zinc-500 mt-0.5">Last sign-in {syd(m.last_login_at)} · Sydney</p>
        </div>
        {!owner && (
          <div className="flex gap-2">
            <button onClick={() => setResetting((r) => !r)} data-testid={`team-reset-${m.id}`} className={`${btn} border border-white/15 text-zinc-300 hover:border-gold hover:text-gold`}><KeyRound size={14} /> Reset password</button>
            <button onClick={remove} data-testid={`team-remove-${m.id}`} className={`${btn} border border-white/15 text-zinc-300 hover:border-red-400 hover:text-red-300`}><Trash2 size={14} /> Remove</button>
          </div>
        )}
      </div>
      {resetting && (
        <div className="mt-3 flex flex-wrap gap-2">
          <input value={pw} onChange={(e) => setPw(e.target.value)} minLength={8} placeholder="New password (8+ chars)" data-testid={`team-reset-input-${m.id}`} className={`${field} max-w-xs`} />
          <button onClick={() => setPw(genPassword())} data-testid={`team-reset-generate-${m.id}`} className="rounded-sm border border-white/15 px-3 text-zinc-300 hover:border-neon hover:text-neon"><RefreshCw size={15} /></button>
          <button onClick={() => copy(pw)} className="rounded-sm border border-white/15 px-3 text-zinc-300 hover:border-neon hover:text-neon"><Copy size={15} /></button>
          <button onClick={resetPassword} disabled={pw.length < 8} data-testid={`team-reset-save-${m.id}`} className={`${btn} bg-neon text-obsidian hover:bg-neon-hover`}>Save password</button>
        </div>
      )}
    </div>
  );
};

export default function AdminTeam() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const load = () => api.get("/admin/team").then((r) => setMembers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="admin-team">
      <div className="rounded-lg border border-gold/20 bg-gold/[0.04] p-5 flex gap-3">
        <ShieldCheck size={20} className="shrink-0 text-gold mt-0.5" />
        <p className="text-sm text-zinc-300">These logins only open <span className="text-white font-medium">this website's admin</span> at <span className="font-mono text-xs">speedyresurfacing.com.au/admin</span>. They are completely separate from your Emergent account — nobody here can see or change the site code, hosting or billing.</p>
      </div>
      <AddMemberForm onAdded={() => load()} />
      <div className="rounded-lg border border-white/10 bg-obsidian-800 overflow-hidden" data-testid="team-list">
        {members.map((m) => <MemberRow key={m.id} m={m} isMe={m.id === user?.id} onChanged={load} />)}
      </div>
    </div>
  );
}
